import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Stack,
  Typography,
} from "@mui/material";
import { IoCheckmark, IoChevronDown } from "react-icons/io5";
import SearchIcon from "@mui/icons-material/Search";
import { useTextDomainContext } from "@/app/providers";
import { string, array, func } from "prop-types";
import { isEqual } from "lodash";
import md5 from "md5";
import { DropdownOption } from "@/types";
import { FabekeDropdownProps } from "./FabekeDropdown.types";

/**
 * TODO: Description here
 * @param {object} props Prop
 * @returns {React.JSX.Element} component
 */
export function FabekeDropdown(props: FabekeDropdownProps) {
  const onChangeRef = useRef(props.onChange);
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);

  const cacheKey = useMemo(() => {
    if (props.componentLabel) {
      return `FabekeDropdown-${props.componentLabel}`;
    } else if (props.options) {
      return `FabekeDropdown-${md5(JSON.stringify(props.options))}`;
    }
    return "";
  }, [props.componentLabel, props.options]);

  // Use useMemo for options to avoid unnecessary state management
  const options = useMemo(() => {
    return props.options && props.options.length > 0
      ? (props.options as DropdownOption[])
      : [
          {
            value: "",
            label: gettext("Select"),
          },
        ];
  }, [props.options, gettext]);

  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedValue, setSelectedValue] = useState<string | number>("");
  const [selectedLabel, setSelectedLabel] = useState<string>(gettext("Select"));

  // Calculate filtered data based on search
  const newData = useMemo(() => {
    if (!searchValue) {
      return options;
    } else {
      return options.filter((item: DropdownOption) => {
        return Object.values(item)
          .join("")
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    }
  }, [options, searchValue]);

  const handleChangeSelectedValue = (value: string) => {
    if (cacheKey && value) {
      localStorage.setItem(cacheKey, value);
    }
    setSelectedValue(value);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSearchValues = (value: string) => {
    setSearchValue(value);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedValue(event.target.value);
    // setSelectedLabel(event.target.label);
    handleChangeSelectedValue(event.target.value);
  };

  useEffect(() => {
    if (selectedValue) {
      const defaultIndex = options.findIndex((x) => x.value === selectedValue);
      if (defaultIndex > -1) {
        const selected = options[defaultIndex];
        if (typeof onChangeRef.current === "function") {
          if (!isEqual(selectedValue, selected.value)) {
            onChangeRef.current(selected);
          }
        }
      }
    }
  }, [options, selectedValue]);

  useEffect(() => {
    const firstSelectedValue = (value: any) => {
      const selectedIndex = options.findIndex((x) => x.value === value);

      if (selectedIndex > -1) {
        const selectedOption = options[selectedIndex];
        setSelectedValue(selectedOption.value);
        setSelectedLabel(selectedOption.label);
        if (typeof onChangeRef.current === "function") {
          onChangeRef.current(selectedOption);
        }
      } else if (props.defaultValue) {
        const defaultIndex = options.findIndex(
          (x) => x.value === props.defaultValue,
        );
        if (defaultIndex > -1) {
          const selectedOption = options[defaultIndex];
          setSelectedValue(selectedOption.value);
          setSelectedLabel(selectedOption.label);
          if (typeof onChangeRef.current === "function") {
            onChangeRef.current(selectedOption);
          }
        }
      }
    };

    const cacheValue = localStorage.getItem(cacheKey);

    if (cacheValue) {
      firstSelectedValue(cacheValue);
    } else if (props.defaultValue) {
      firstSelectedValue(props.defaultValue);
    }
  }, [cacheKey, props.defaultValue, options]);

  return (
    <Grid size={{ xs: 12, sm: "auto" }}>
      <Button
        id="basic-button-2"
        size="small"
        aria-controls={open ? "basic-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        endIcon={<IoChevronDown size={12} />}
        variant="outlined"
        onClick={handleClick}
        sx={{
          height: "32px",
        }}
      >
        <Typography
          sx={{
            color: "grey.600",
            fontWeight: "fontWeightMedium",
            fontSize: "0.875rem",
          }}
        >
          {props.componentLabel}
        </Typography>
        {selectedLabel && (
          <Chip
            sx={{
              ml: 0.5,
              backgroundColor: "transparent",
              fontSize: "0.875rem",
            }}
            size="small"
            label={
              <Typography
                sx={{
                  fontSize: "0.875rem",
                }}
              >
                {gettext(selectedLabel)}
              </Typography>
            }
          />
        )}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        sx={{
          "& .MuiMenu-paper": {
            scrollbarWidth: "thin",
            boxSizing: "borderBox",
            position: "relative",
            width: "282px",
            maxHeight: "264px",
            left: "6403px",
            top: "310px",
            background: "#FFFFFF",
            border: "1px solid #E4E4E7",
            boxShadow:
              "0px 4px 6px -2px rgba(0, 0, 0, 0.06), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
            borderRadius: "8px",
            "*::-webkit-scrollbar": {
              position: "absolute",
              width: "0.1em",
              height: "240px",
              left: "224px",
              top: "8px",
            },
            "*::-webkit-scrollbar-track": {
              backgroundColor: "#E4E4E7",
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: "#27272A",
              outline: "2px solid #27272A",
              height: "64px",
            },
          },
          "& .MuiMenuItem-root": {
            fontWeight: 400,
            fontSize: "0.875rem",
            height: "40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderRadius: "4px",
            mx: 1,
          },
          "& .MuiMenuItem-root.Mui-selected": {
            backgroundColor: "#FFFFFF",
          },
        }}
      >
        <Stack spacing={1}>
          <OutlinedInput
            sx={{
              color: "grey.600",
              mx: 1,

              "& .css-hq24ym-MuiOutlinedInput-notchedOutline": {
                borderRadius: "4px",
              },
            }}
            size="small"
            value={searchValue}
            onChange={(e) => handleSearchValues(e.target.value)}
            placeholder={gettext("Search")}
            startAdornment={
              <InputAdornment position={"start"}>
                <SearchIcon />
              </InputAdornment>
            }
          />
          <Box>
            {newData && newData.length > 0 ? (
              newData?.map((option: any, index: number) => (
                <MenuItem
                  key={option.value + "_" + index}
                  selected={selectedValue === option.value}
                  title={gettext(option.label)}
                  onClick={() => {
                    handleChangeSelectedValue(option.value);
                    setSelectedLabel(option.label);
                    handleChange(option);
                    handleClose();
                  }}
                >
                  <Typography
                    sx={{
                      maxWidth: selectedValue === option.value ? "90%" : "100%",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {gettext(option.label)}
                  </Typography>

                  {selectedValue === option.value && (
                    <IoCheckmark size={14} color="#064b7c" />
                  )}
                </MenuItem>
              ))
            ) : (
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: "400",
                  margin: "12px",
                }}
              >
                {gettext("Not Found")}
              </Typography>
            )}
          </Box>
        </Stack>
      </Menu>
    </Grid>
  );
}

FabekeDropdown.propTypes = {
  componentLabel: string,
  options: array,
  defaultValue: string,
  onChange: func,
};
